import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';

export interface InstructorAuthProfile {
  id: string;
  user_id: string;
  name: string;
  email: string;
  phone: string | null;
  website: string | null;
  location: string | null;
  bio: string | null;
  avatar_url: string | null;
  expertise: string[] | null;
  social_links: Record<string, any>;
  is_active: boolean;
  permissions: string[];
  created_at: string;
  updated_at: string;
}

export interface AuthResponse {
  success: boolean;
  user?: User;
  instructor?: InstructorAuthProfile;
  token?: string;
  error?: string;
}

export interface PermissionCheckResult {
  hasPermission: boolean;
  permissions: string[];
  instructor?: InstructorAuthProfile;
}

class InstructorAuthService {
  /**
   * Authentifier un instructeur
   */
  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      // 1. Authentification via Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (authError) {
        return {
          success: false,
          error: this.formatAuthError(authError.message)
        };
      }

      if (!authData.user || !authData.session) {
        return {
          success: false,
          error: 'Erreur d\'authentification'
        };
      }

      // 2. Vérifier que l'utilisateur a le rôle instructor
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('id, role, is_active')
        .eq('id', authData.user.id)
        .single();

      if (userError || !user) {
        await supabase.auth.signOut();
        return {
          success: false,
          error: 'Utilisateur non trouvé'
        };
      }

      if (user.role !== 'instructor') {
        await supabase.auth.signOut();
        return {
          success: false,
          error: 'Accès refusé. Vous n\'êtes pas autorisé en tant qu\'instructeur.'
        };
      }

      if (!user.is_active) {
        await supabase.auth.signOut();
        return {
          success: false,
          error: 'Votre compte utilisateur est inactif. Contactez l\'administrateur.'
        };
      }

      // 3. Récupérer le profil instructeur avec permissions
      const instructorProfile = await this.getInstructorProfileWithPermissions(authData.user.id);
      
      if (!instructorProfile) {
        await supabase.auth.signOut();
        return {
          success: false,
          error: 'Profil instructeur non trouvé ou inactif'
        };
      }

      return {
        success: true,
        user: authData.user,
        instructor: instructorProfile,
        token: authData.session.access_token
      };

    } catch (error) {
      console.error('Erreur lors de la connexion instructeur:', error);
      return {
        success: false,
        error: 'Une erreur inattendue s\'est produite'
      };
    }
  }

  /**
   * Déconnexion
   */
  async logout(): Promise<void> {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  }

  /**
   * Vérifier la session actuelle
   */
  async checkSession(): Promise<AuthResponse> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session?.user) {
        return {
          success: false,
          error: 'Session expirée'
        };
      }

      // Vérifier que l'utilisateur est toujours un instructeur actif
      const instructorProfile = await this.getInstructorProfileWithPermissions(session.user.id);
      
      if (!instructorProfile) {
        await supabase.auth.signOut();
        return {
          success: false,
          error: 'Profil instructeur non trouvé ou inactif'
        };
      }

      return {
        success: true,
        user: session.user,
        instructor: instructorProfile,
        token: session.access_token
      };

    } catch (error) {
      console.error('Erreur lors de la vérification de session:', error);
      return {
        success: false,
        error: 'Erreur de vérification de session'
      };
    }
  }

  /**
   * Récupérer le profil instructeur avec permissions
   */
  async getInstructorProfileWithPermissions(userId: string): Promise<InstructorAuthProfile | null> {
    try {
      // Tenter d'abord la vue étendue (si créée), sinon fallback sur la vue existante
      let data: any = null;
      let error: any = null;
      {
        const res = await supabase
          .from('instructor_auth_view_ext')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();
        if (!res.error && res.data) {
          data = res.data;
        } else {
          // Fallback sur la vue d'origine
          const res2 = await supabase
            .from('instructor_auth_view')
            .select('*')
            .eq('user_id', userId)
            .single();
          data = res2.data as any;
          error = res2.error as any;
        }
      }

      if (error || !data) {
        console.error('Erreur lors de la récupération du profil instructeur:', error);
        return null;
      }

      // Fallback: si la vue n'expose pas phone/website/location, aller les chercher directement
      let phone: string | null = (data as any).phone ?? null;
      let website: string | null = (data as any).website ?? null;
      let location: string | null = (data as any).location ?? null;

      if (phone === null && website === null && location === null) {
        try {
          const { data: extra, error: extraError } = await supabase
            .from('elearn_instructors')
            .select('phone, website, location')
            .eq('id', data.instructor_id)
            .single();
          if (!extraError && extra) {
            phone = extra.phone ?? null;
            website = extra.website ?? null;
            location = extra.location ?? null;
          }
        } catch (e) {
          // silencieux: ce fallback ne doit pas casser la récupération principale
        }
      }

      return {
        id: data.instructor_id,
        user_id: data.user_id,
        name: data.instructor_name,
        email: data.email,
        phone,
        website,
        location,
        bio: data.bio || null,
        avatar_url: data.avatar_url || null,
        expertise: data.expertise || null,
        social_links: data.social_links || {},
        is_active: data.instructor_active,
        permissions: data.permissions || [],
        created_at: data.instructor_created_at || '',
        updated_at: data.instructor_updated_at || ''
      };

    } catch (error) {
      console.error('Exception lors de la récupération du profil instructeur:', error);
      return null;
    }
  }

  /**
   * Vérifier une permission spécifique
   */
  async checkPermission(userId: string, permission: string): Promise<PermissionCheckResult> {
    try {
      const { data, error } = await supabase
        .rpc('check_instructor_permission', {
          p_user_id: userId,
          p_permission_type: permission
        });

      if (error) {
        console.error('Erreur lors de la vérification de permission:', error);
        return {
          hasPermission: false,
          permissions: []
        };
      }

      // Récupérer toutes les permissions pour le contexte
      const instructorProfile = await this.getInstructorProfileWithPermissions(userId);

      return {
        hasPermission: data === true,
        permissions: instructorProfile?.permissions || [],
        instructor: instructorProfile || undefined
      };

    } catch (error) {
      console.error('Exception lors de la vérification de permission:', error);
      return {
        hasPermission: false,
        permissions: []
      };
    }
  }

  /**
   * Vérifier plusieurs permissions
   */
  async checkPermissions(userId: string, permissions: string[], requireAll: boolean = false): Promise<PermissionCheckResult> {
    try {
      const instructorProfile = await this.getInstructorProfileWithPermissions(userId);
      
      if (!instructorProfile) {
        return {
          hasPermission: false,
          permissions: []
        };
      }

      const userPermissions = instructorProfile.permissions;
      let hasPermission = false;

      if (requireAll) {
        // Toutes les permissions sont requises
        hasPermission = permissions.every(perm => userPermissions.includes(perm));
      } else {
        // Au moins une permission est requise
        hasPermission = permissions.some(perm => userPermissions.includes(perm));
      }

      return {
        hasPermission,
        permissions: userPermissions,
        instructor: instructorProfile
      };

    } catch (error) {
      console.error('Exception lors de la vérification des permissions:', error);
      return {
        hasPermission: false,
        permissions: []
      };
    }
  }

  /**
   * Mettre à jour le profil instructeur
   */
  async updateProfile(instructorId: string, updates: Partial<InstructorAuthProfile>): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('elearn_instructors')
        .update({
          name: updates.name,
          phone: updates.phone,
          website: updates.website,
          location: updates.location,
          bio: updates.bio,
          avatar_url: updates.avatar_url,
          expertise: updates.expertise,
          social_links: updates.social_links,
          updated_at: new Date().toISOString()
        })
        .eq('id', instructorId);

      if (error) {
        console.error('Erreur lors de la mise à jour du profil:', error);
        return false;
      }

      return true;

    } catch (error) {
      console.error('Exception lors de la mise à jour du profil:', error);
      return false;
    }
  }

  /**
   * Formater les erreurs d'authentification
   */
  private formatAuthError(errorMessage: string): string {
    const errorMap: Record<string, string> = {
      'Invalid login credentials': 'Email ou mot de passe incorrect',
      'Email not confirmed': 'Veuillez confirmer votre email avant de vous connecter',
      'Too many requests': 'Trop de tentatives de connexion. Veuillez réessayer plus tard.',
      'User not found': 'Utilisateur non trouvé',
      'Invalid email': 'Adresse email invalide',
      'Weak password': 'Le mot de passe est trop faible'
    };

    return errorMap[errorMessage] || errorMessage;
  }

  /**
   * Réinitialiser le mot de passe
   */
  async resetPassword(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/instructor/reset-password`
      });

      if (error) {
        return {
          success: false,
          error: this.formatAuthError(error.message)
        };
      }

      return { success: true };

    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      return {
        success: false,
        error: 'Une erreur inattendue s\'est produite'
      };
    }
  }

  /**
   * Changer le mot de passe
   */
  async changePassword(newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        return {
          success: false,
          error: this.formatAuthError(error.message)
        };
      }

      return { success: true };

    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      return {
        success: false,
        error: 'Une erreur inattendue s\'est produite'
      };
    }
  }
}

export const instructorAuthService = new InstructorAuthService();
export default instructorAuthService;
